import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CreditCard,
  AccountBalance,
  PhoneAndroid,
  Money,
} from '@mui/icons-material';

interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  methodType: 'card' | 'transfer' | 'cash' | 'mobile' | 'wallet';
  processingFee: number;
  minAmount?: number;
  maxAmount?: number;
  status: 'active' | 'inactive';
  sortOrder: number;
  createdAt: string;
}

// Mock data
const mockMethods: PaymentMethod[] = [
  {
    id: '1',
    code: 'CARD_VISA',
    name: 'Cartão Visa',
    description: 'Pagamento com cartão Visa',
    icon: 'credit_card',
    methodType: 'card',
    processingFee: 250,
    minAmount: 100,
    maxAmount: 5000000,
    status: 'active',
    sortOrder: 1,
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    code: 'CARD_MC',
    name: 'Cartão Mastercard',
    description: 'Pagamento com cartão Mastercard',
    icon: 'credit_card',
    methodType: 'card',
    processingFee: 250,
    minAmount: 100,
    maxAmount: 5000000,
    status: 'active',
    sortOrder: 2,
    createdAt: '2024-01-01',
  },
  {
    id: '3',
    code: 'TRANSFER',
    name: 'Transferência Bancária',
    description: 'Transferência bancária directa',
    icon: 'account_balance',
    methodType: 'transfer',
    processingFee: 0,
    minAmount: 1000,
    status: 'active',
    sortOrder: 3,
    createdAt: '2024-01-01',
  },
  {
    id: '4',
    code: 'MBWAY',
    name: 'MB WAY',
    description: 'Pagamento via MB WAY',
    icon: 'phone_android',
    methodType: 'mobile',
    processingFee: 150,
    minAmount: 50,
    maxAmount: 2000000,
    status: 'active',
    sortOrder: 4,
    createdAt: '2024-01-01',
  },
  {
    id: '5',
    code: 'MULTIBANCO',
    name: 'Multibanco',
    description: 'Referência Multibanco',
    icon: 'receipt',
    methodType: 'transfer',
    processingFee: 100,
    minAmount: 100,
    maxAmount: 10000000,
    status: 'active',
    sortOrder: 5,
    createdAt: '2024-01-01',
  },
  {
    id: '6',
    code: 'CASH',
    name: 'Dinheiro',
    description: 'Pagamento em dinheiro',
    icon: 'money',
    methodType: 'cash',
    processingFee: 0,
    minAmount: 10,
    maxAmount: 1000000,
    status: 'active',
    sortOrder: 6,
    createdAt: '2024-01-01',
  },
  {
    id: '7',
    code: 'PAYPAL',
    name: 'PayPal',
    description: 'Pagamento via PayPal',
    icon: 'account_balance_wallet',
    methodType: 'wallet',
    processingFee: 350,
    minAmount: 100,
    maxAmount: 10000000,
    status: 'active',
    sortOrder: 7,
    createdAt: '2024-03-01',
  },
  {
    id: '8',
    code: 'CHEQUE',
    name: 'Cheque',
    description: 'Pagamento por cheque',
    icon: 'receipt_long',
    methodType: 'cash',
    processingFee: 0,
    minAmount: 5000,
    status: 'inactive',
    sortOrder: 8,
    createdAt: '2024-01-01',
  },
];

const PaymentMethods: React.FC = () => {
  const { t } = useTranslation();
  const [methods, setMethods] = useState<PaymentMethod[]>(mockMethods);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    description: string;
    icon: string;
    methodType: 'card' | 'transfer' | 'cash' | 'mobile' | 'wallet';
    processingFee: number;
    minAmount: number;
    maxAmount: number;
    status: 'active' | 'inactive';
    sortOrder: number;
  }>({
    code: '',
    name: '',
    description: '',
    icon: 'payment',
    methodType: 'card',
    processingFee: 0,
    minAmount: 0,
    maxAmount: 0,
    status: 'active',
    sortOrder: 1,
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCard fontSize="small" />;
      case 'transfer':
        return <AccountBalance fontSize="small" />;
      case 'mobile':
        return <PhoneAndroid fontSize="small" />;
      case 'cash':
        return <Money fontSize="small" />;
      default:
        return <PaymentIcon fontSize="small" />;
    }
  };

  const getTypeChip = (type: string) => {
    const config: Record<string, { color: 'primary' | 'secondary' | 'info' | 'success' | 'warning'; label: string }> = {
      card: { color: 'primary', label: t('settings.paymentMethods.card') },
      transfer: { color: 'info', label: t('settings.paymentMethods.transfer') },
      cash: { color: 'success', label: t('settings.paymentMethods.cash') },
      mobile: { color: 'secondary', label: t('settings.paymentMethods.mobile') },
      wallet: { color: 'warning', label: t('settings.paymentMethods.wallet') },
    };
    const { color, label } = config[type] || config.card;
    return <Chip size="small" color={color} label={label} icon={getTypeIcon(type)} />;
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const filteredMethods = methods.filter(method => {
    const matchesSearch = searchTerm === '' ||
      method.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      method.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      method.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || method.methodType === typeFilter;
    const matchesStatus = statusFilter === 'all' || method.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: methods.length,
    active: methods.filter(m => m.status === 'active').length,
    card: methods.filter(m => m.methodType === 'card').length,
    transfer: methods.filter(m => m.methodType === 'transfer').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (method?: PaymentMethod) => {
    if (method) {
      setSelectedMethod(method);
      setFormData({
        code: method.code,
        name: method.name,
        description: method.description,
        icon: method.icon,
        methodType: method.methodType,
        processingFee: method.processingFee,
        minAmount: method.minAmount || 0,
        maxAmount: method.maxAmount || 0,
        status: method.status,
        sortOrder: method.sortOrder,
      });
    } else {
      setSelectedMethod(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        icon: 'payment',
        methodType: 'card',
        processingFee: 0,
        minAmount: 0,
        maxAmount: 0,
        status: 'active',
        sortOrder: methods.length + 1,
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedMethod(null);
  };

  const handleSave = () => {
    if (selectedMethod) {
      setMethods(methods.map(m =>
        m.id === selectedMethod.id
          ? {
              ...m,
              ...formData,
              minAmount: formData.minAmount > 0 ? formData.minAmount : undefined,
              maxAmount: formData.maxAmount > 0 ? formData.maxAmount : undefined,
            }
          : m
      ));
    } else {
      const newMethod: PaymentMethod = {
        id: crypto.randomUUID(),
        ...formData,
        minAmount: formData.minAmount > 0 ? formData.minAmount : undefined,
        maxAmount: formData.maxAmount > 0 ? formData.maxAmount : undefined,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setMethods([...methods, newMethod]);
    }
    handleCloseEdit();
  };

  const handleDelete = (method: PaymentMethod) => {
    if (window.confirm(t('settings.paymentMethods.confirmDelete', { name: method.name }))) {
      setMethods(methods.filter(m => m.id !== method.id));
    }
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value / 100).toLocaleString('pt-PT')}$`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('settings.paymentMethods.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('settings.paymentMethods.newMethod')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.paymentMethods.totalMethods')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.paymentMethods.activeFemale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.card}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.paymentMethods.cards')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalance color="warning" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.transfer}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.paymentMethods.transfers')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('settings.paymentMethods.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.type')}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="card">{t('settings.paymentMethods.card')}</MenuItem>
                <MenuItem value="transfer">{t('settings.paymentMethods.transfer')}</MenuItem>
                <MenuItem value="cash">{t('settings.paymentMethods.cash')}</MenuItem>
                <MenuItem value="mobile">{t('settings.paymentMethods.mobile')}</MenuItem>
                <MenuItem value="wallet">{t('settings.paymentMethods.wallet')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('settings.banks.activeMale')}</MenuItem>
                <MenuItem value="inactive">{t('settings.banks.inactiveMale')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Methods Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>
                <TableCell width={120}>{t('common.code')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell width={150}>{t('common.type')}</TableCell>
                <TableCell width={120} align="right">{t('settings.paymentMethods.fee')}</TableCell>
                <TableCell width={120} align="right">{t('settings.paymentMethods.min')}</TableCell>
                <TableCell width={120} align="right">{t('settings.paymentMethods.max')}</TableCell>
                <TableCell width={100}>{t('common.status')}</TableCell>
                <TableCell width={100} align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMethods
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((method) => (
                <TableRow key={method.id} hover>
                  <TableCell>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                      {getTypeIcon(method.methodType)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.75rem">
                      {method.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {method.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {method.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTypeChip(method.methodType)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontSize="0.75rem">
                      {formatCurrency(method.processingFee)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {method.minAmount ? (
                      <Typography variant="body2" fontSize="0.75rem">
                        {formatCurrency(method.minAmount)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {method.maxAmount ? (
                      <Typography variant="body2" fontSize="0.75rem">
                        {formatCurrency(method.maxAmount)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(method.status)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(method)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(method)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredMethods.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon />
          {selectedMethod ? t('settings.paymentMethods.editMethod') : t('settings.paymentMethods.newMethod')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 20 }}
                disabled={!!selectedMethod}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={formData.methodType}
                onChange={(e) => setFormData({ ...formData, methodType: e.target.value as any })}
              >
                <MenuItem value="card">{t('settings.paymentMethods.card')}</MenuItem>
                <MenuItem value="transfer">{t('settings.paymentMethods.transfer')}</MenuItem>
                <MenuItem value="cash">{t('settings.paymentMethods.cash')}</MenuItem>
                <MenuItem value="mobile">{t('settings.paymentMethods.mobile')}</MenuItem>
                <MenuItem value="wallet">{t('settings.paymentMethods.wallet')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.paymentMethods.processingFee')}
                value={formData.processingFee}
                onChange={(e) => setFormData({ ...formData, processingFee: parseInt(e.target.value) || 0 })}
                helperText={`${Math.round(formData.processingFee / 100).toLocaleString('pt-PT')}$`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.paymentMethods.minAmount')}
                value={formData.minAmount}
                onChange={(e) => setFormData({ ...formData, minAmount: parseInt(e.target.value) || 0 })}
                helperText={formData.minAmount > 0 ? `${Math.round(formData.minAmount / 100).toLocaleString('pt-PT')}$` : t('common.optional')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.paymentMethods.maxAmount')}
                value={formData.maxAmount}
                onChange={(e) => setFormData({ ...formData, maxAmount: parseInt(e.target.value) || 0 })}
                helperText={formData.maxAmount > 0 ? `${Math.round(formData.maxAmount / 100).toLocaleString('pt-PT')}$` : t('common.optional')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.paymentMethods.sortOrder')}
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="active">{t('common.active')}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.code || !formData.name}
          >
            {selectedMethod ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethods;
